import { ColumnFiltersState } from "@design-system/Table";
import {
    AiAnswerStatusFilter,
    QuestionStatusFilter,
    QuestionType,
} from "@generated/client/graphql";
import { useAtom } from "jotai";
import { useMemo, useState, type FC } from "react";
import { FilterChips } from "../../../FilterChips";
import { columnFiltersAtom } from "./ctx";

export const extractValueFromFilter = (
    colFilter: ColumnFiltersState,
    colId: string,
) => {
    const { value: rawValue } = colFilter?.find(
        (filter) => filter.id === colId,
    ) ?? { value: [] };

    if (!Array.isArray(rawValue)) {
        return [];
    }

    return rawValue;
};

export const updateFilter = (id: string, value: (string | null)[]) => {
    return (prevFilters: ColumnFiltersState) => {
        const columnFilter = prevFilters.find((filter) => filter.id === id) ?? {
            id,
            value: [],
        };
        return [
            ...prevFilters.filter((filter) => filter.id !== id),
            { ...columnFilter, value },
        ];
    };
};

export const QuestionsTableFilters: FC = () => {
    const [filterValue, setFilterValue] = useAtom(columnFiltersAtom);
    const selectedUserIds: string[] = useMemo(() => {
        return extractValueFromFilter(filterValue, "owner");
    }, [filterValue]);
    const selectedQuestionStatuses: QuestionStatusFilter[] = useMemo(() => {
        return extractValueFromFilter(filterValue, "status");
    }, [filterValue]);

    const selectedAnswerType: QuestionType[] = useMemo(() => {
        return extractValueFromFilter(filterValue, "questionType");
    }, [filterValue]);

    const selectedAiAnswerStatuses: AiAnswerStatusFilter[] = useMemo(() => {
        return extractValueFromFilter(filterValue, "aiAnswer");
    }, [filterValue]);

    const handleOwnerChange = (value: (string | null)[]) => {
        setFilterValue(updateFilter("owner", value));
    };

    const handleStatusChange = (value: (string | null)[]) => {
        setFilterValue(updateFilter("status", value));
    };

    const handleAnswerTypeChange = (value: (string | null)[]) => {
        setFilterValue(updateFilter("questionType", value));
    };

    const handleAiAnswerStatusChange = (value: (string | null)[]) => {
        setFilterValue(updateFilter("aiAnswer", value));
    };

    const [userSearchString, setUserSearchString] = useState<
        string | undefined
    >(undefined);

    return (
        <FilterChips>
            <FilterChips.QuestionStatusFilterChip
                value={selectedQuestionStatuses}
                onValueChange={handleStatusChange}
            />
            <FilterChips.AnswerTypeFilterChip
                value={selectedAnswerType}
                onValueChange={handleAnswerTypeChange}
            />
            <FilterChips.AiAnswerStatusFilterChip
                value={selectedAiAnswerStatuses}
                onValueChange={handleAiAnswerStatusChange}
            />
            <FilterChips.AssigneeFilterChip
                value={selectedUserIds}
                onValueChange={handleOwnerChange}
                searchFilter={userSearchString}
                onSearchFilterChange={setUserSearchString}
                includeNone
            />
        </FilterChips>
    );
};
